export interface Feature {
  title: string;
  body: string;
}

export interface ProjectContent {
  tagline: string;
  features?: Feature[];
  steps?: string[];
}

export const projectContent: Record<string, ProjectContent> = {
  shipmate: {
    tagline: "Your AI voice companion",
    features: [
      { title: "Spoken notifications", body: "From every Claude Code session. Build finished, tests broke, a question needs an answer — hear it, don't miss it." },
      { title: "Delegate by voice", body: "\"Tell the main repo session to run the tests.\" Shipmate routes work to the right session and reports back." },
      { title: "Persistent memory", body: "Remembers your projects, decisions, and work patterns — available across every session, every device." },
      { title: "GitHub by voice", body: "Create, read, close, and comment on issues while you cook, walk, or think. No keyboard required." },
    ],
    steps: [
      "Install the Shipmate plugin in Claude Code.",
      "Open Shipmate in your browser or add it to your home screen.",
      "Start talking. Shipmate listens, remembers, and speaks back.",
    ],
  },
  mnemo: {
    tagline: "Memory for machines",
    features: [
      { title: "Semantic memory", body: "Facts, concepts, and knowledge — stored as embeddings in pgvector and recalled by meaning, not keywords." },
      { title: "Episodic memory", body: "What happened, when, and in what context. Session recaps, decisions, and the reasoning behind them." },
      { title: "Procedural memory", body: "How things are done — workflows, code patterns, and operational knowledge that agents reuse across sessions." },
      { title: "Embedded, not standalone", body: "Mnemo isn't an app you sign into. It's an engine that lives inside agents like Adam and BP Agent." },
    ],
  },
  notetaker: {
    tagline: "Your meetings, remembered",
    features: [
      { title: "Auto-transcribe", body: "Joins meetings, listens quietly, and produces a full transcript. You focus on the conversation." },
      { title: "Memory extraction", body: "Key decisions, follow-ups, and context get pushed into Mnemo — available to every agent automatically." },
      { title: "GitHub issues", body: "Action items surface as GitHub issues with the right labels. No copy-paste, no missed tasks." },
    ],
    steps: [
      "Invite the notetaker bot to your meeting.",
      "It transcribes and identifies action items in real time.",
      "Memories and issues are created automatically when the call ends.",
    ],
  },
  forge: {
    tagline: "Distil what you know",
    features: [
      { title: "Guided interview", body: "An AI voice agent asks you thoughtful questions about something you care about. You talk, it listens." },
      { title: "Knowledge distillation", body: "Your answers are synthesised into something structured — a document, a tool, a set of principles." },
      { title: "From conversation to creation", body: "The output isn't a transcript. It's a useful artefact that captures what you actually meant." },
    ],
    steps: [
      "Pick a topic — something you know well or care deeply about.",
      "Have a conversation with the AI interviewer.",
      "Get back a distilled, useful artefact built from your words.",
    ],
  },
  innbox: {
    tagline: "Email without the anxiety",
    features: [
      { title: "Unique data views", body: "See your inbox as data, not a to-do list. Group by sender, by thread age, by reply urgency." },
      { title: "Calm by design", body: "No red badges, no unread counts screaming at you. Information when you want it, silence when you don't." },
      { title: "Smart routing", body: "Rules that actually work — route, archive, and surface emails based on patterns, not just keywords." },
    ],
  },
  "dnd-dojo": {
    tagline: "Solo D&D, voice-powered",
    features: [
      { title: "AI party members", body: "Generate companions with personalities, backstories, and tactical preferences. They fight alongside you." },
      { title: "Voice narration", body: "Combat encounters narrated aloud — hear the action unfold instead of reading walls of text." },
      { title: "Tactical hex grid", body: "A proper battle map. Position matters, flanking matters, terrain matters." },
    ],
  },
  "knowledge-tree": {
    tagline: "Learn like a civilisation",
    features: [
      { title: "Hex skill tree", body: "A Civilization-style tech tree for real skills. Unlock nodes, see prerequisites, track mastery." },
      { title: "Two-sided platform", body: "Learners traverse the tree. Mentors plant the nodes — writing lessons, challenges, and assessments." },
      { title: "Visible progress", body: "Not another course list. A spatial map of what you know and what's next." },
    ],
  },
  portal: {
    tagline: "Your news, once a day",
    features: [
      { title: "Curated sources", body: "X, Reddit, Google News — only the feeds you actually want, distilled into one page." },
      { title: "Once-a-day cadence", body: "No infinite scroll, no refresh habit. One summary, published each morning." },
      { title: "No filler", body: "AI summarisation strips the noise. You get signal, not engagement bait." },
    ],
  },
  "artefact-editor": {
    tagline: "Edit what agents produce",
    features: [
      { title: "Direct manipulation", body: "Tweak, refine, and recompose AI outputs instead of re-prompting and hoping." },
      { title: "Works with any agent", body: "Documents, code, images — if an agent made it, the editor can work with it." },
    ],
  },
  "see-you-in-the-morning": {
    tagline: "Bedtime stories, illustrated by AI",
    features: [
      { title: "Generate stories", body: "A new story every night, tailored to your child's interests and reading level." },
      { title: "AI illustration", body: "Each story comes with illustrations generated to match the narrative." },
      { title: "Build a library", body: "Save favourites, revisit old ones, build a personal collection." },
    ],
  },
  relay: {
    tagline: "One API for all your messages",
    features: [
      { title: "Multi-channel", body: "Telegram, WhatsApp, and SMS from a single endpoint. No per-platform plumbing." },
      { title: "Shared service", body: "Every app in the brainsteam estate sends messages through Relay. One config, one set of credentials." },
    ],
  },
  "help-people-help": {
    tagline: "Tools for sharing what you know",
    features: [
      { title: "A concept, not an app", body: "Help People Help is a thread that connects several projects — Forge, Finding Rat Park, Knowledge Tree." },
      { title: "Distil and share", body: "The goal: let people take what they know, package it, and share it with others at scale." },
    ],
  },
};
